import {ColorSchemeType} from 'platform/components';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import {useGetLeadQualityOptionsQuery} from '@dms/api/sales';
import {LeadQualityEnum, LeadQualityEnumSchema} from '@dms/api/shared';
import {
  useChangeWorkshopInterestLeadQualityMutation,
  useGetWorkshopInterestQuery,
} from '@dms/api/workshop';
import {
  EntityHeaderDropdownFlag,
  handleApiError,
  useWorkshopInterestEditPermissions,
} from '@dms/shared';

import {buildArray, Nullish} from 'shared/web';

const getLeadQualityColorScheme = (value: string | Nullish) =>
  match<string | Nullish, ColorSchemeType>(value)
    .with('blue', always('blue'))
    .with('yellow', always('yellow'))
    .with('red', always('red'))
    .with('gray', always('neutral'))
    .otherwise(always('neutral'));

export function useWorkshopInterestDropdownFlags(interestId: string) {
  const {data: workshopInterest} = useGetWorkshopInterestQuery({id: interestId});
  const {data: leadQualityOptions} = useGetLeadQualityOptionsQuery();
  const [changeWorkshopInterestLeadQuality, {isLoading: isChangeLeadQualityLoading}] =
    useChangeWorkshopInterestLeadQualityMutation();
  const {hasChangeLeadQualityPermission} = useWorkshopInterestEditPermissions({interestId});

  const onChangeWorkshopInterestLeadQuality = (leadQuality: LeadQualityEnum) => {
    changeWorkshopInterestLeadQuality({
      id: interestId,
      changeLeadQualityRequestBody: {
        leadQuality,
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const currentLeadQuality = leadQualityOptions?.find(
    (option) => option.value === workshopInterest?.leadQuality
  );

  const filteredOptions = (leadQualityOptions ?? []).flatMap((option) => {
    const parsedValue = LeadQualityEnumSchema.safeParse(option.value);

    return parsedValue.success && parsedValue.data !== workshopInterest?.leadQuality
      ? [{...option, value: parsedValue.data}]
      : [];
  });

  return buildArray<EntityHeaderDropdownFlag>().add({
    label: currentLeadQuality?.label ?? '',
    colorScheme: getLeadQualityColorScheme(currentLeadQuality?.color),
    isDisabled: !hasChangeLeadQualityPermission || isChangeLeadQualityLoading,
    isLoading: isChangeLeadQualityLoading,
    dropdownItems: filteredOptions.map((option) => ({
      label: option.label,
      onClick: () => {
        onChangeWorkshopInterestLeadQuality(option.value);
      },
      flag: {
        label: option.label,
        colorScheme: getLeadQualityColorScheme(option.color),
        isSubtle: false,
      },
    })),
  });
}
